import { json } from 'react-router-dom'
import { AxiosError } from 'axios'
import { validateRequestToken } from '@/routes/resetPassword/actions.ts'
import { User } from '@/store/user.ts'

export type ResetPasswordTokenData = { user: User } | { invalid: true } | { unavailable: true }

/**
 * Three outcomes, because collapsing them lies to the user in one direction or
 * the other. An expired or revoked token used to come back as `null` and the
 * component rendered an empty page — no message, no way to ask for a new link.
 * Saying "expired" for every failure is the opposite mistake: a 500 or a dead
 * connection would then tell someone their perfectly good link is dead, and the
 * one thing they would do about it (request another) cannot help.
 *
 * So: only a response the server actually sent and that rejects the token (4xx)
 * means `invalid`. No response at all, or a 5xx, means we could not check —
 * `unavailable`, which is worth retrying with the same link.
 *
 * Anything that is not an AxiosError is re-thrown rather than swallowed: those
 * are real crashes and belong to the router's error boundary. Falling off the
 * end of the function (the original behaviour) turned them into `undefined`,
 * i.e. the same blank page by a different route.
 */
// eslint-disable-next-line
export async function ResetPasswordTokenLoader({ params }: { params: any }) {
  const token = params.token
  if (!token) return json<ResetPasswordTokenData>({ invalid: true })
  try {
    const response = await validateRequestToken(token)
    return json(response.data)
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status
      const tokenWasRejected = status !== undefined && status >= 400 && status < 500
      return json<ResetPasswordTokenData>(
        tokenWasRejected ? { invalid: true } : { unavailable: true },
      )
    }
    throw error
  }
}
